import apiClient from '../utils/apiClient';

/**
 * Projections Analysis Service
 * 
 * Service for accessing projection accuracy and backtesting data
 */

const projectionsAnalysisService = {
  /**
   * Get aggregate accuracy metrics across all schools
   * Can accept either individual parameters or a params object for flexibility
   */
  async getAggregateAccuracy(teamId = null, scenario = 'median', minYears = 4) {
    const params = new URLSearchParams();
    if (teamId) params.append('team_id', teamId);
    params.append('scenario', scenario);
    params.append('min_years', minYears);

    return await apiClient.get(`/projections-analysis/aggregate?${params}`);
  },

  /**
   * Get aggregate data with flexible params object
   * @param {Object} params - Query parameters (team_id, scenario, min_years, etc.)
   */
  async getAggregateData(params = {}) {
    return await apiClient.get('/projections-analysis/aggregate', { params });
  },

  /**
   * Get accuracy metrics for a specific school
   */
  async getSchoolAccuracy(schoolUuid) {
    return await apiClient.get(`/projections-analysis/school/${schoolUuid}`);
  },

  /**
   * Get detailed walkthrough analysis for a specific school
   */
  async getSchoolDetailedAnalysis(schoolUuid) {
    return await apiClient.get(`/projections-analysis/school/${schoolUuid}/detailed`);
  },

  /**
   * List all schools with backtesting data
   */
  async listSchools(teamId = null, minYears = 4, minAccuracy = null) {
    const params = new URLSearchParams();
    if (teamId) params.append('team_id', teamId);
    params.append('min_years', minYears);
    if (minAccuracy !== null) params.append('min_accuracy', minAccuracy);

    return await apiClient.get(`/projections-analysis/schools?${params}`);
  },

  /**
   * Trigger backtesting for schools
   */
  async runBacktest(schoolUuids = null, teamId = null, minYears = 4, limit = null) {
    const body = {
      ...(schoolUuids && { school_uuids: schoolUuids }),
      ...(teamId && { team_id: teamId }),
      min_years: minYears,
      ...(limit && { limit })
    };

    return await apiClient.post('/projections-analysis/run-backtest', body);
  },

  /**
   * Export accuracy data
   */
  async exportData(schoolUuids = null, format = 'json', scenario = 'median') {
    const params = new URLSearchParams();
    if (schoolUuids && schoolUuids.length > 0) {
      params.append('school_uuids', schoolUuids.join(','));
    }
    params.append('format', format);
    params.append('scenario', scenario);

    if (format === 'csv') {
      // For CSV, handle as blob download
      const blob = await apiClient.get(`/projections-analysis/export?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `projections_accuracy_${scenario}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      return { status: 'success', message: 'Download started' };
    } else {
      // For JSON, return the data (interceptor already extracts response.data)
      return await apiClient.get(`/projections-analysis/export?${params}`);
    }
  }
};

export { projectionsAnalysisService };
export default projectionsAnalysisService;

